import { useState, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormGuard } from "@/hooks/useFormGuard";
import { UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";
import { useCreateTenant, type TenantInsert } from "@/hooks/useTenants";
import { useUnifiedInvite } from "@/hooks/useUnifiedInvite";
import { getSupabaseErrorMessage } from "@/lib/supabase-errors";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/auth/PhoneInput";
import { normalizePhoneForDB, isValidSaudiPhone } from "@/lib/phone";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { InvitationSuccessModal } from "@/components/invitations/InvitationSuccessModal";

interface AddTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (tenantId: string) => void;
}

const idTypes = [
  { value: "national", labelAr: "هوية وطنية", labelEn: "National ID" },
  { value: "resident", labelAr: "إقامة", labelEn: "Resident" },
  { value: "gcc", labelAr: "خليجي", labelEn: "GCC" },
  { value: "passport", labelAr: "جواز سفر", labelEn: "Passport" },
  { value: "diplomat", labelAr: "دبلوماسي", labelEn: "Diplomat" },
  { value: "visitor", labelAr: "زائر", labelEn: "Visitor" },
  { value: "premium_resident", labelAr: "إقامة مميزة", labelEn: "Premium Resident" },
  { value: "other", labelAr: "أخرى", labelEn: "Other" },
];

const nationalities = [
  { value: "SA", labelAr: "سعودي", labelEn: "Saudi" },
  { value: "EG", labelAr: "مصري", labelEn: "Egyptian" },
  { value: "JO", labelAr: "أردني", labelEn: "Jordanian" },
  { value: "SY", labelAr: "سوري", labelEn: "Syrian" },
  { value: "YE", labelAr: "يمني", labelEn: "Yemeni" },
  { value: "PK", labelAr: "باكستاني", labelEn: "Pakistani" },
  { value: "IN", labelAr: "هندي", labelEn: "Indian" },
  { value: "BD", labelAr: "بنغلاديشي", labelEn: "Bangladeshi" },
  { value: "PH", labelAr: "فلبيني", labelEn: "Filipino" },
  { value: "other", labelAr: "أخرى", labelEn: "Other" },
];

export function AddTenantDialog({ open, onOpenChange, onSuccess }: AddTenantDialogProps) {
  const { t, language } = useLanguage();
  const createTenant = useCreateTenant();
  const unifiedInvite = useUnifiedInvite();

  const [tenantType, setTenantType] = useState<"individual" | "commercial">("individual");
  const [formError, setFormError] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<{
    inviteLink: string;
    whatsappMessage: string;
    recipientName: string;
    recipientPhone: string;
    recipientEmail?: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    first_name_en: "",
    last_name_en: "",
    commercial_name: "",
    commercial_name_en: "",
    unified_number: "",
    cr_number: "",
    vat_number: "",
    phone: "",
    alternative_phone: "",
    email: "",
    id_type: "national",
    id_number: "",
    date_of_birth: "",
    nationality: "",
    address: "",
    short_address: "",
    national_address: "",
    country: "",
    region: "",
    city: "",
    district: "",
    street_name: "",
    building_number: "",
    additional_code: "",
    postal_code: "",
    notification_channel: "email",
  } as TenantInsert);

  // Compute dirty state from key fields
  const isDirty = useMemo(() => !!(
    formData.first_name || formData.last_name || formData.phone ||
    formData.email || formData.id_number || formData.commercial_name
  ), [formData.first_name, formData.last_name, formData.phone, formData.email, formData.id_number, formData.commercial_name]);
  const blocker = useFormGuard(open && isDirty);

  // Date-of-birth bounds: a DOB can't be in the future, and a 120-year lookback
  // keeps the year dropdown from flooring at the legacy currentYear-50 (~1976),
  // so a tenant born before 1976 can be entered.
  const dobMaxDate = useMemo(() => new Date(), []);
  const dobMinDate = useMemo(() => new Date(dobMaxDate.getFullYear() - 120, 0, 1), [dobMaxDate]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.first_name || !formData.last_name || !formData.phone || !formData.id_number) {
      toast.error(t("يرجى تعبئة جميع الحقول المطلوبة", "Please fill all required fields"));
      return;
    }

    if (!isValidSaudiPhone(formData.phone)) {
      toast.error(t("رقم الجوال غير صالح", "Invalid mobile number"));
      return;
    }

    if (formData.alternative_phone && !isValidSaudiPhone(formData.alternative_phone)) {
      toast.error(t("رقم الجوال البديل غير صالح", "Invalid alternative mobile number"));
      return;
    }

    if (tenantType === "commercial" && (!formData.commercial_name || !formData.cr_number)) {
      toast.error(t("يرجى تعبئة بيانات المنشأة التجارية", "Please fill commercial entity details"));
      return;
    }

    const canonicalPhone = normalizePhoneForDB(formData.phone)!;
    const canonicalAltPhone = normalizePhoneForDB(formData.alternative_phone) ?? undefined;

    try {
      const newTenant = await createTenant.mutateAsync({
        tenant_type: tenantType,
        first_name: formData.first_name,
        last_name: formData.last_name,
        first_name_en: formData.first_name_en || undefined,
        last_name_en: formData.last_name_en || undefined,
        commercial_name: tenantType === "commercial" ? formData.commercial_name : undefined,
        commercial_name_en: tenantType === "commercial" ? formData.commercial_name_en : undefined,
        unified_number: tenantType === "commercial" ? formData.unified_number : undefined,
        cr_number: tenantType === "commercial" ? formData.cr_number : undefined,
        vat_number: tenantType === "commercial" ? formData.vat_number || undefined : undefined,
        phone: canonicalPhone,
        alternative_phone: canonicalAltPhone,
        email: formData.email || undefined,
        id_type: formData.id_type,
        id_number: formData.id_number,
        date_of_birth: formData.date_of_birth || undefined,
        nationality: formData.nationality || undefined,
        address: formData.address || undefined,
        short_address: formData.short_address || undefined,
        national_address: formData.national_address || undefined,
        country: formData.country || undefined,
        region: formData.region || undefined,
        city: formData.city || undefined,
        district: formData.district || undefined,
        street_name: formData.street_name || undefined,
        building_number: formData.building_number || undefined,
        additional_code: formData.additional_code || undefined,
        postal_code: formData.postal_code || undefined,
        notification_channel: formData.notification_channel,
        is_active: true,
      });

      toast.success(
        newTenant?._reused
          ? t("هذا المستأجر موجود مسبقًا — تم اختياره", "This tenant already exists — selected")
          : t("تم إضافة المستأجر بنجاح", "Tenant added successfully")
      );
      onOpenChange(false);

      // Auto-invite: generate the invite link/token (no SMS or email is sent here —
      // delivery is opt-in from the InvitationSuccessModal that opens next).
      // Skip entirely when the user explicitly chose "none".
      if (newTenant?.id && canonicalPhone && formData.notification_channel !== "none") {
        try {
          const tenantName =
            (tenantType === "commercial"
              ? formData.commercial_name
              : `${formData.first_name} ${formData.last_name}`).trim();
          const result = await unifiedInvite.mutateAsync({
            profileType: "tenant",
            entityId: newTenant.id,
            fullName: tenantName,
            phone: canonicalPhone,
          });
          setInviteResult({
            inviteLink: result.inviteLink,
            whatsappMessage: result.whatsappMessage,
            recipientName: tenantName,
            recipientPhone: canonicalPhone,
            recipientEmail: formData.email || undefined,
          });
        } catch (inviteErr: unknown) {
          // Tenant was saved; the invitation is a follow-up. Don't fail the
          // whole flow, but surface WHY the invite didn't go out (this used to
          // be swallowed silently — the "create invitation" error in the ticket).
          const { ar, en } = getSupabaseErrorMessage(inviteErr, {
            ar: "تم حفظ المستأجر، لكن تعذّر إنشاء الدعوة",
            en: "Tenant saved, but the invitation could not be created",
          });
          toast.error(t(ar, en));
        }
      }

      // Call onSuccess callback with new tenant ID
      if (onSuccess && newTenant?.id) {
        onSuccess(newTenant.id);
      }

      // Reset form
      setFormData({
        first_name: "",
        last_name: "",
        first_name_en: "",
        last_name_en: "",
        commercial_name: "",
        commercial_name_en: "",
        unified_number: "",
        cr_number: "",
        vat_number: "",
        phone: "",
        alternative_phone: "",
        email: "",
        id_type: "national",
        id_number: "",
        date_of_birth: "",
        nationality: "",
        address: "",
        short_address: "",
        national_address: "",
        country: "",
        region: "",
        city: "",
        district: "",
        street_name: "",
        building_number: "",
        additional_code: "",
        postal_code: "",
        notification_channel: "email",
      } as TenantInsert);
      setTenantType("individual");
    } catch (error: unknown) {
      const { ar, en } = getSupabaseErrorMessage(error, {
        ar: "حدث خطأ أثناء إضافة المستأجر",
        en: "Error adding tenant",
      });
      const msg = t(ar, en);
      setFormError(msg);
      toast.error(msg);
    }
  };

  return (
    <>
    {/* Invite success modal is handled by useUnifiedInvite internally */}
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{t("إضافة مستأجر", "Add Tenant")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("أدخل بيانات المستأجر الجديد الشخصية وبيانات الهوية ومعلومات التواصل لإضافته إلى النظام.", "Enter the new tenant's personal details, identity information, and contact details to add them to the system.")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <ScrollArea className="h-[calc(90vh-180px)] pe-4">
            {/* Tenant Type Tabs */}
            <Tabs value={tenantType} onValueChange={(v) => setTenantType(v as "individual" | "commercial")} className="mb-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="individual">{t("فرد", "Individual")}</TabsTrigger>
                <TabsTrigger value="commercial">{t("منشأة تجارية", "Commercial")}</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="space-y-6">
              {/* Commercial Fields */}
              {tenantType === "commercial" && (
                <div className="space-y-4 p-4 rounded-lg bg-muted/50">
                  <h3 className="font-semibold text-sm">{t("بيانات المنشأة", "Commercial Details")}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t("اسم المنشأة", "Commercial Name")} *</Label>
                      <Input
                        value={formData.commercial_name}
                        onChange={(e) => handleChange("commercial_name", e.target.value)}
                        placeholder={t("اسم المنشأة بالعربي", "Commercial name in Arabic")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("اسم المنشأة (إنجليزي)", "Commercial Name (English)")}</Label>
                      <Input
                        value={formData.commercial_name_en}
                        onChange={(e) => handleChange("commercial_name_en", e.target.value)}
                        placeholder="Commercial name in English"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t("الرقم الموحد", "Unified Number")} *</Label>
                      <Input
                        value={formData.unified_number}
                        onChange={(e) => handleChange("unified_number", e.target.value)}
                        placeholder="7000000000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("رقم السجل التجاري", "CR Number")} *</Label>
                      <Input
                        value={formData.cr_number}
                        onChange={(e) => handleChange("cr_number", e.target.value)}
                        placeholder="1010000000"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 col-span-2">
                      <Label>{t("الرقم الضريبي", "VAT Number")}</Label>
                      <Input
                        value={formData.vat_number}
                        onChange={(e) => handleChange("vat_number", e.target.value)}
                        placeholder="300000000000003"
                        dir="ltr"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">{t("البيانات الشخصية", "Personal Information")}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("الاسم الأول", "First Name")} *</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => handleChange("first_name", e.target.value)}
                      placeholder={t("الاسم الأول", "First name")}
                      data-testid="tenant-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("الاسم الأخير", "Last Name")} *</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => handleChange("last_name", e.target.value)}
                      placeholder={t("الاسم الأخير", "Last name")}
                      data-testid="tenant-last-name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("الاسم الأول (إنجليزي)", "First Name (English)")}</Label>
                    <Input
                      value={formData.first_name_en}
                      onChange={(e) => handleChange("first_name_en", e.target.value)}
                      placeholder="First name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("الاسم الأخير (إنجليزي)", "Last Name (English)")}</Label>
                    <Input
                      value={formData.last_name_en}
                      onChange={(e) => handleChange("last_name_en", e.target.value)}
                      placeholder="Last name"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">{t("معلومات التواصل", "Contact Information")}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("رقم الجوال (أبشر)", "Mobile Number (Absher)")} *</Label>
                    <div data-testid="tenant-phone-input">
                      <PhoneInput
                        value={formData.phone}
                        onChange={(v) => handleChange("phone", v)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("رقم جوال بديل", "Alternative Mobile")}</Label>
                    <PhoneInput
                      value={formData.alternative_phone}
                      onChange={(v) => handleChange("alternative_phone", v)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("البريد الإلكتروني", "Email")}</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="email@example.com"
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Identity Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">{t("بيانات الهوية", "Identity Information")}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("نوع الهوية", "ID Type")} *</Label>
                    <Select
                      value={formData.id_type}
                      onValueChange={(value) => handleChange("id_type", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("اختر نوع الهوية", "Select ID type")} />
                      </SelectTrigger>
                      <SelectContent>
                        {idTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {t(type.labelAr, type.labelEn)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("رقم الهوية", "ID Number")} *</Label>
                    <Input
                      value={formData.id_number}
                      onChange={(e) => handleChange("id_number", e.target.value)}
                      placeholder="1000000000"
                      data-testid="tenant-id-number"
                      dir="ltr"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("تاريخ الميلاد", "Date of Birth")}</Label>
                    <DatePicker
                      value={formData.date_of_birth}
                      onChange={(date) => handleChange("date_of_birth", date)}
                      locale={language === "ar" ? "ar" : "en"}
                      minDate={dobMinDate}
                      maxDate={dobMaxDate}
                      showHijri
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("الجنسية", "Nationality")}</Label>
                    <Select
                      value={formData.nationality}
                      onValueChange={(value) => handleChange("nationality", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("اختر الجنسية", "Select nationality")} />
                      </SelectTrigger>
                      <SelectContent>
                        {nationalities.map((nat) => (
                          <SelectItem key={nat.value} value={nat.value}>
                            {t(nat.labelAr, nat.labelEn)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">{t("العنوان", "Address")}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("العنوان المختصر", "Short Address")}</Label>
                    <Input
                      value={formData.short_address}
                      onChange={(e) => handleChange("short_address", e.target.value)}
                      placeholder={t("العنوان المختصر", "Short address")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("العنوان الوطني", "National Address")}</Label>
                    <Input
                      value={formData.national_address}
                      onChange={(e) => handleChange("national_address", e.target.value)}
                      placeholder={t("العنوان الوطني", "National address")}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t("المنطقة", "Region")}</Label>
                    <Input
                      value={formData.region}
                      onChange={(e) => handleChange("region", e.target.value)}
                      placeholder={t("المنطقة", "Region")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("المدينة", "City")}</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                      placeholder={t("المدينة", "City")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("الحي", "District")}</Label>
                    <Input
                      value={formData.district}
                      onChange={(e) => handleChange("district", e.target.value)}
                      placeholder={t("الحي", "District")}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("اسم الشارع", "Street Name")}</Label>
                    <Input
                      value={formData.street_name}
                      onChange={(e) => handleChange("street_name", e.target.value)}
                      placeholder={t("اسم الشارع", "Street name")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("رقم المبنى", "Building Number")}</Label>
                    <Input
                      value={formData.building_number}
                      onChange={(e) => handleChange("building_number", e.target.value)}
                      placeholder={t("رقم المبنى", "Building number")}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("الرمز الإضافي", "Additional Code")}</Label>
                    <Input
                      value={formData.additional_code}
                      onChange={(e) => handleChange("additional_code", e.target.value)}
                      placeholder="0000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("الرمز البريدي", "Postal Code")}</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => handleChange("postal_code", e.target.value)}
                      placeholder="00000"
                    />
                  </div>
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">{t("إشعار تفعيل الحساب عبر", "Account Activation Notification Via")}</h3>
                <RadioGroup
                  value={formData.notification_channel}
                  onValueChange={(value) => handleChange("notification_channel", value)}
                  className="flex gap-6"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="email" id="email" />
                    <Label htmlFor="email">{t("البريد الإلكتروني", "Email")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="sms" id="sms" />
                    <Label htmlFor="sms">{t("رسالة نصية", "SMS")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="both" id="both" />
                    <Label htmlFor="both">{t("كلاهما", "Both")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="none" id="none" data-testid="tenant-notify-none" />
                    <Label htmlFor="none">{t("بدون إشعار", "Skip notification")}</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </ScrollArea>

          {formError && (
            <p className="text-sm text-destructive pt-3">{formError}</p>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("إلغاء", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={createTenant.isPending || unifiedInvite.isPending}
              data-testid="tenant-submit"
            >
              {(createTenant.isPending || unifiedInvite.isPending) && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {t("إضافة وإرسال دعوة", "Add & Invite")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    <UnsavedChangesDialog blocker={blocker} />
    {inviteResult && (
      <InvitationSuccessModal
        open={!!inviteResult}
        onOpenChange={(o) => { if (!o) setInviteResult(null); }}
        inviteLink={inviteResult.inviteLink}
        whatsappMessage={inviteResult.whatsappMessage}
        recipientPhone={inviteResult.recipientPhone}
        recipientName={inviteResult.recipientName}
        recipientEmail={inviteResult.recipientEmail}
        profileType="tenant"
      />
    )}
    </>
  );
}
